import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Warehouse } from "lucide-react";
import DockDoorSettings from "../TabContent/Calendar-Main/DockDoorSettings";

const AddDockModal = ({ warehouse, onClose, onSave }) => {
  const [dockName, setDockName] = useState("");
  const [sameAsWarehouse, setSameAsWarehouse] = useState(false);
  const [copiedFromWarehouse, setCopiedFromWarehouse] = useState(false);
  const [settings, setSettings] = useState({
    regularHours: {},
    holidays: [],
    blackouts: [],
  });

  const sanitizeSettings = (baseSettings = {}) => {
    const clone = JSON.parse(JSON.stringify(baseSettings));
    // Strip IDs so new dock edits don’t delete/update warehouse events
    clone.holidays = (clone.holidays || []).map((h) => ({
      ...h,
      eventId: undefined,
    }));
    clone.blackouts = (clone.blackouts || []).map((b) => ({
      ...b,
      exceptionId: undefined,
    }));
    return clone;
  };

  // Default Dock Name
  useEffect(() => {
    const count =
      (warehouse?.docks?.length || 0) +
      (warehouse?.additionalDocks?.length || 0) +
      1;
    setDockName(`Dock ${count}`);
  }, [warehouse]);

  // When copying settings from warehouse
  useEffect(() => {
    if (copiedFromWarehouse && warehouse?.settings) {
      setSettings(sanitizeSettings(warehouse.settings));
    }
  }, [copiedFromWarehouse]);

  const handleSave = () => {
    onSave({
      dockId: Date.now(),
      dockName,
      sameAsWarehouse,
      copiedFromWarehouse,
      settings: sameAsWarehouse ? null : settings,
    });
  };

  // Toggle component (Apple-style)
  const Toggle = ({ checked, onChange }) => (
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        className="sr-only peer"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <div
        className="
          w-11 h-6 
          bg-gray-300 peer-checked:bg-[#1e2a74] 
          rounded-full peer 
          transition-all duration-300 
          after:content-[''] 
          after:absolute after:top-0.5 after:left-0.5 
          after:bg-white after:border-gray-300 after:border 
          after:rounded-full 
          after:h-5 after:w-5 
          after:transition-all
          peer-checked:after:translate-x-5
        "
      ></div>
    </label>
  );

  return (
    <motion.div
      className="fixed inset-0 bg-black/40 z-[1000] flex items-center justify-center px-fluid-3 py-fluid-3"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
    <motion.div
        className="
            bg-white rounded-2xl shadow-xl 
            w-full 
            max-w-[95vw]              /* small screens */
            md:max-w-[800px]          /* tablets */
            lg:max-w-[1000px]         /* laptop */
            xl:max-w-[1200px]         /* big laptop */
            2xl:max-w-[1400px]        /* large screen */
            max-h-[95vh] 
            overflow-y-auto
            p-fluid-4 space-y-fluid-4
            border border-gray-200
        "
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        >


        {/* ================= HEADER ================= */}
        <div className="flex flex-col items-center space-y-fluid-2 border-b pb-fluid-3">
          <div className="bg-[#1e2a74] rounded-full p-fluid-2 text-white shadow-md">
            <Warehouse size={30} />
          </div>

          <h2 className="text-fluid-xl font-semibold text-[#111827]">
            Add New Dock
          </h2>

          <p className="text-fluid-base text-gray-600 text-center max-w-[620px]">
            Create a new dock and configure its scheduling & capacity settings.
          </p>
        </div>

        {/* ================= MAIN FORM ================= */}
        <div className="space-y-fluid-4">

          {/* === Dock Name + Toggles (1 row, 3 columns) === */}
          <div className="grid grid-cols-[1fr,1fr,1fr] gap-fluid-3 items-center">

            {/* Dock Name */}
            <div className="space-y-fluid-1">
              <input
                type="text"
                value={dockName}
                onChange={(e) => setDockName(e.target.value)}
                className="
                  w-full border border-gray-300 rounded-xl 
                 p-fluid-2 text-fluid-sm
                  focus:outline-none focus:ring-2 focus:ring-[#223688]
                  shadow-sm
                "
              />
            </div>

            {/* Same as Warehouse Toggle */}
            {/* <div className="border rounded-xl p-fluid-2 bg-gray-50 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-fluid-sm font-medium">Same as warehouse</span>
                <Toggle
                  checked={sameAsWarehouse}
                  onChange={(checked) => {
                    setSameAsWarehouse(checked);
                    if (checked) setCopiedFromWarehouse(false);
                  }}
                />
              </div>
            </div> */}

            {/* Copy from Warehouse Toggle */}
            <div className="border rounded-xl p-fluid-2 bg-gray-50 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-fluid-sm font-medium">Copy from warehouse</span>
                <Toggle
                  checked={copiedFromWarehouse}
                  onChange={(checked) => {
                    setCopiedFromWarehouse(checked);
                    if (!checked) {
                      setSettings({
                        regularHours: {},
                        holidays: [],
                        blackouts: [],
                      });
                    }
                    if (checked) setSameAsWarehouse(false);
                  }}
                />
              </div>
            </div>
          </div>

          {/* Dock Door Settings (only when custom settings are needed) */}
          {!sameAsWarehouse && (
            <div className="border rounded-xl p-fluid-3 bg-white shadow-sm">
                <DockDoorSettings
                settings={settings}
                onChange={(newSettings) => setSettings(newSettings)}
                isViewOnly={false}     // copy is still editable
                isWarehouseView={false}
                isNewDock
                disableTabRedirect
                />
            </div>
            )}

        </div>

        {/* ================= FOOTER ================= */}
        <div className="flex justify-end gap-fluid-3 pt-fluid-3 border-t border-gray-200">
          <button
            onClick={onClose}
            className="
              px-fluid-button-x py-fluid-button-y 
              border border-gray-300 rounded-lg 
              text-gray-700 hover:bg-gray-100 
              text-fluid-sm transition
            "
          >
            Cancel
          </button>

          <button
            onClick={handleSave}
            disabled={!dockName.trim()}
            className={`
              px-fluid-button-x py-fluid-button-y 
              text-fluid-sm rounded-lg font-semibold transition
              ${dockName.trim()
                ? "bg-[#1e2a74] hover:bg-[#18225c] text-white"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }
            `}
          >
            Save Dock
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AddDockModal;
